import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Primary funnel CTA. Gradient solid, animated arrow, colored shadow.
 * Reference: docs/design-guidelines.md §3
 *
 * Variants:
 *  - default: standard funnel button
 *  - lg: hero / above-the-fold dominant CTA
 *  - outline: secondary alternative (used on Pricing self-service)
 */
type Variant = "default" | "lg" | "outline";

export default function PrimaryCTA({
  to,
  children,
  className,
  variant = "default",
  showArrow = true,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
  variant?: Variant;
  showArrow?: boolean;
}) {
  const base =
    "group inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200 font-body";
  const size = variant === "lg" ? "px-8 py-4 text-base md:text-lg" : "px-6 py-3 text-sm md:text-base";

  const skin =
    variant === "outline"
      ? "border border-white/15 bg-white/5 text-slate-100 hover:bg-white/10 hover:border-white/25"
      : [
          "text-primary-foreground",
          "bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B]",
          "shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.55)]",
          "hover:shadow-[0_18px_44px_-12px_hsl(var(--primary)/0.7)]",
          "hover:scale-[1.02] active:scale-[0.99]",
        ].join(" ");

  return (
    <Link to={to} className={cn(base, size, skin, className)}>
      <span>{children}</span>
      {showArrow && (
        <ArrowRight
          className={cn(
            "h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5",
            variant === "lg" && "h-5 w-5"
          )}
        />
      )}
    </Link>
  );
}
